import React from "react";

// Lightweight shareable popup. Drop-in for any badge you want to flex.
export function BadgeShareModal({ badge, leagueName, onClose, onToast }) {
  if (!badge) return null;

  const url = typeof window !== "undefined" ? window.location.origin : "";
  const text = `🎖️ I just unlocked the "${badge.name}" badge${
    leagueName ? ` in ${leagueName}` : ""
  } on LEAGUE HUB! ${badge.description || ""}`.trim();

  const shareLinks = [
    {
      key: "x",
      label: "X (Twitter)",
      icon: "fab fa-x-twitter",
      color: "border-white/30 hover:border-white",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    },
    {
      key: "discord",
      label: "Discord",
      icon: "fab fa-discord",
      color: "border-[#5865F2]/60 hover:border-[#5865F2]",
      // Discord has no share intent — copy the message and open Discord.
      onClick: async () => {
        try {
          await navigator.clipboard?.writeText(`${text} ${url}`);
          onToast?.("📋 Copied for Discord — paste it in any channel.");
        } catch {
          onToast?.("Could not copy. Long-press the card.");
        }
      },
    },
    {
      key: "telegram",
      label: "Telegram",
      icon: "fab fa-telegram",
      color: "border-[#14b8a6]/40 hover:border-[#14b8a6]",
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
    {
      key: "twitch",
      label: "Twitch",
      icon: "fab fa-twitch",
      color: "border-[#9146FF]/50 hover:border-[#9146FF]",
      onClick: async () => {
        try {
          await navigator.clipboard?.writeText(`${text} ${url}`);
          onToast?.("📋 Copied for Twitch chat.");
        } catch {
          onToast?.("Copy failed.");
        }
      },
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(`${text} ${url}`);
      onToast?.("🔗 Link copied.");
    } catch {
      onToast?.("Copy failed.");
    }
  };

  const tier = (badge.tier || "bronze").toLowerCase();
  const ring =
    tier === "diamond" ? "border-[#14b8a6]" :
    tier === "gold" ? "border-[#14b8a6]" :
    tier === "silver" ? "border-slate-400" :
    "border-[#14b8a6]";
  const text_c =
    tier === "diamond" ? "text-[#14b8a6]" :
    tier === "gold" ? "text-[#14b8a6]" :
    tier === "silver" ? "text-slate-300" :
    "text-[#14b8a6]0";

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-md p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="badge-share-backdrop"
    >
      <div
        className="glass neon-glow-pink rounded-3xl p-7 w-full max-w-md text-center"
        onClick={(e) => e.stopPropagation()}
        data-testid="badge-share-modal"
      >
        <div className={`mx-auto w-24 h-24 rounded-full border-4 ${ring} flex items-center justify-center animate-glow-pulse`}>
          <i className={`fas ${badge.icon || "fa-medal"} text-4xl ${text_c}`} />
        </div>
        <p className={`mt-4 text-[10px] tracking-[3px] ${text_c}`}>{tier.toUpperCase()} TIER</p>
        <h3 className="font-display text-3xl grad-text mt-1" data-testid="badge-share-name">
          {badge.name}
        </h3>
        <p className="text-sm text-white/70 mt-2">{badge.description}</p>

        <p className="text-[10px] tracking-widest text-[#14b8a6] mt-6 mb-3">📣 SHARE TO SOCIALS</p>
        <div className="grid grid-cols-2 gap-2">
          {shareLinks.map((s) => (
            s.href ? (
              <a
                key={s.key}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`badge-share-${s.key}`}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border bg-black/40 ${s.color} text-sm font-bold tracking-wider transition-colors`}
              >
                <i className={s.icon} /> {s.label}
              </a>
            ) : (
              <button
                key={s.key}
                onClick={s.onClick}
                data-testid={`badge-share-${s.key}`}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-2xl border bg-black/40 ${s.color} text-sm font-bold tracking-wider transition-colors`}
              >
                <i className={s.icon} /> {s.label}
              </button>
            )
          ))}
        </div>
        <button
          onClick={copy}
          data-testid="badge-share-copy"
          className="mt-3 w-full btn-ghost rounded-2xl px-4 py-3 text-xs tracking-widest"
        >
          <i className="fas fa-link mr-1" /> COPY LINK + MESSAGE
        </button>

        <button
          onClick={onClose}
          data-testid="badge-share-close"
          className="mt-4 text-xs text-white/50 hover:text-white"
        >
          close
        </button>
      </div>
    </div>
  );
}

export default BadgeShareModal;
